import { useState, useCallback, useRef } from 'react';
import { ALL_STANDARD_CARD_KEYS, ALL_VENGEANCE_CARD_KEYS, SHINY_TIERS, cardKeyToCard, isActionCard } from '@flip7/shared';
import type { Card as CardType, ShinyTier } from '@flip7/shared';
import { Card } from '../components/Card';
import { useShinyCards } from '../contexts/ShinyCardsContext';

interface ShinyCollectionScreenProps {
  onBack: () => void;
}

const ALL_KEYS = [...new Set([...ALL_STANDARD_CARD_KEYS, ...ALL_VENGEANCE_CARD_KEYS])];

const TIER_LABELS: Record<ShinyTier, string> = {
  'holo': 'Holo',
  'reverse-holo': 'Reverse Holo',
  'full-art': 'Full Art',
  'alt-art': 'Alt Art',
  'gold-secret': 'Gold Secret',
};

const TIER_COLORS: Record<ShinyTier, string> = {
  'holo': 'rgba(180, 200, 255, 0.8)',
  'reverse-holo': 'rgba(200, 180, 240, 0.8)',
  'full-art': 'var(--gold)',
  'alt-art': 'rgba(200, 130, 255, 0.9)',
  'gold-secret': 'rgba(255, 215, 0, 0.95)',
};

/** Which cards can appear at each tier */
function getKeysForTier(tier: ShinyTier): string[] {
  if (tier === 'alt-art') {
    // Only action + special cards
    return ALL_KEYS.filter(key => {
      const card = cardKeyToCard(key);
      return isActionCard(card) || (card.type === 'number' && 'special' in card && !!card.special);
    });
  }
  // All other tiers: every card
  return ALL_KEYS;
}

/** Total collectible (card, tier) pairs */
const TOTAL_COLLECTIBLE = SHINY_TIERS.reduce((sum, tier) => sum + getKeysForTier(tier).length, 0);

function ShinyInspector({ card, tier, onClose }: { card: CardType; tier: ShinyTier; onClose: () => void }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [gradPos, setGradPos] = useState({ x: 50, y: 50 });

  const handleMove = useCallback((clientX: number, clientY: number) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    setTilt({ x: (y - 0.5) * -20, y: (x - 0.5) * 20 });
    setGradPos({ x: x * 100, y: y * 100 });
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    if (t) handleMove(t.clientX, t.clientY);
  }, [handleMove]);

  const resetTilt = useCallback(() => {
    setTilt({ x: 0, y: 0 });
    setGradPos({ x: 50, y: 50 });
  }, []);

  return (
    <div className="shiny-inspector__backdrop" onClick={onClose}>
      <div
        className="shiny-inspector__card-wrap"
        onClick={e => e.stopPropagation()}
        onMouseMove={handleMouseMove}
        onMouseLeave={resetTilt}
        onTouchMove={handleTouchMove}
        onTouchEnd={resetTilt}
        ref={cardRef}
        style={{
          transform: `perspective(600px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        }}
      >
        <Card card={card} shinyTier={tier} />
        <div
          className="shiny-inspector__holo"
          style={{
            background: `radial-gradient(circle at ${gradPos.x}% ${gradPos.y}%, rgba(255,215,0,0.3) 0%, rgba(255,105,180,0.2) 25%, rgba(0,191,255,0.2) 50%, rgba(50,205,50,0.15) 75%, transparent 100%)`,
          }}
        />
      </div>
      <p className="shiny-inspector__tier-label" style={{ color: TIER_COLORS[tier] }}>
        {TIER_LABELS[tier]}
      </p>
    </div>
  );
}

export function ShinyCollectionScreen({ onBack }: ShinyCollectionScreenProps) {
  const { shinyMap, totalOwned } = useShinyCards();
  const [inspecting, setInspecting] = useState<{ card: CardType; tier: ShinyTier } | null>(null);

  return (
    <div className="hero-panel">
      <div className="screen screen--padded-top content-narrow" style={{ padding: 'var(--space-xl) var(--space-lg)' }}>
        <div className="page-header" style={{ marginBottom: 'var(--space-xl)' }}>
          <button onClick={onBack} className="back-btn">&larr;</button>
          <h1 className="section-header" style={{ flex: 1, fontSize: '1.6rem' }}>Shinies</h1>
          <span style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1rem',
            color: 'var(--gold)',
          }}>
            {totalOwned} / {TOTAL_COLLECTIBLE}
          </span>
        </div>

        {SHINY_TIERS.map((tier, tIdx) => {
          const keys = getKeysForTier(tier);
          const ownedCount = keys.filter(k => shinyMap.get(k)?.includes(tier)).length;
          return (
            <div key={tier} className="ornate-border" style={{
              padding: '14px 12px',
              marginBottom: tIdx < SHINY_TIERS.length - 1 ? 16 : 0,
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
              }}>
                <h3 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '0.8rem',
                  letterSpacing: '0.08em',
                  color: TIER_COLORS[tier],
                  margin: 0,
                }}>
                  {TIER_LABELS[tier]}
                </h3>
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: 'var(--text-muted)',
                }}>
                  {ownedCount} / {keys.length}
                </span>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(48px, 1fr))',
                gap: 5,
              }}>
                {keys.map(key => {
                  const card = cardKeyToCard(key);
                  const owned = !!shinyMap.get(key)?.includes(tier);
                  return (
                    <div
                      key={key}
                      style={{
                        opacity: owned ? 1 : 0.25,
                        filter: owned ? 'none' : 'grayscale(1)',
                        transition: 'opacity 0.2s, filter 0.2s',
                        cursor: owned ? 'pointer' : 'default',
                      }}
                      onClick={() => owned && setInspecting({ card, tier })}
                    >
                      <Card card={card} shinyTier={owned ? tier : undefined} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {inspecting && (
          <ShinyInspector
            card={inspecting.card}
            tier={inspecting.tier}
            onClose={() => setInspecting(null)}
          />
        )}
      </div>
    </div>
  );
}
